
'use client';

import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo } from 'react';
import { format as formatDate } from 'date-fns';
import { getConversionRates, watchConversionRates } from '@/lib/currencies';
import { currencies } from '@/lib/currency-data';
import { format as formatMoney, fromDecimal } from '@/lib/money';
import { useAuth, type UserSettings } from './auth-context';
import { useGeoCountry } from './geo-context';
import { currencyForCountry, detectLocaleCurrency } from '@/lib/detect-currency';
import { CURRENCY_LOCALE, formatDisplayCurrency } from '@/lib/display-currency-format';
import { hasUsableRate, preferGeoCurrency, serverDisplayCurrency } from '@/lib/display-rates';
import { useHydrated } from '@/hooks/use-hydrated';
import {
  browserGuestCurrencyStorage,
  guestCurrencyAfterStorageEvent,
  initialGuestCurrency,
  isOfferedCurrency,
  writeGuestCurrency,
} from '@/lib/guest-currency';
import { setToastDurationMs } from '@/hooks/use-toast';
import { detectTimezone } from '@/lib/timezones';
import { accentLabelHex, hexToHsl, PAGE_HEX, readableAccentHex, visibleAccentHex } from '@/lib/color-utils';
import { pageBackgroundPalette } from '@/lib/page-background';
import { normaliseThemePreference } from '@/lib/theme-preference';
import { useProductDesignOverride } from './product-design-context';
import { isHeldCurrency, BUYER_FX_SURCHARGE_RATE } from '@/lib/platform-fees';
import {
  BACKGROUND_DEFAULTS,
  BACKGROUND_RESET_PATCH,
  backgroundPrefs,
  gradientColorSetting,
  gradientDirectionSetting,
  normaliseGradientColor,
  normaliseGradientDirection,
  resolveGradientColor,
  resolveGradientDirection,
  type GradientDirection,
} from '@/lib/background-prefs';

type Theme = 'dark' | 'light' | 'system';
type ResolvedTheme = 'dark' | 'light';
type DateFormat = 'DD/MM/YYYY' | 'YYYY/MM/DD' | 'MM/DD/YYYY';

/** localStorage key used by both the pre-hydration script (in root layout) and the provider to keep `<html>` in sync across reloads / before React hydrates. */
export const THEME_PREF_STORAGE_KEY = 'themePref';

/** localStorage key for the accent colour. The pre-hydration script in the
 *  root layout reads this and sets the --primary / --ring / --chart-1 CSS
 *  variables before React mounts so the user's accent paints immediately
 *  on reload without a flash of the default accent. */
export const ACCENT_COLOR_STORAGE_KEY = 'accentColor';

/** The default accent in the LIGHT theme, hsl(241 90% 67%), and the brand
 *  colour wherever one value has to stand for both themes (the manifest, the
 *  embeddable buy button, a seller's "default accent"). The dark theme paints
 *  its lighter sibling, DEFAULT_ACCENT_COLOR_DARK, the same hue (owner decision
 *  2026-10-23; before that one violet, #773fe3, served both). Keep it
 *  lowercase; the Reset buttons compare against it after lowercasing the
 *  current value.
 *
 *  This value is duplicated in places that cannot import it, and every copy
 *  has to move together:
 *   - `src/app/globals.css`, as the `:root` `--primary` / `--ring` /
 *     `--chart-1` / `--gradient` tokens (and `--primary-readable`, which is
 *     readableAccentHex of this value: 241 90% 63%) that paint before React
 *     mounts. The `.dark` block carries DEFAULT_ACCENT_COLOR_DARK the same way.
 *   - `src/app/manifest.ts` as `theme_color`.
 *   - `functions/src/index.ts`, `createVerifiedUserProfile`, which stamps it
 *     on every new account's settings (the functions tree shares no modules
 *     with this one, and it needs a functions deploy to move).
 *   - The comment on the pre-hydration script in `src/app/layout.tsx`.
 *   - The embeddable buy button's brand theme: `BUTTON_THEMES.brand` in
 *     `src/lib/payment-link.ts` (background and border), and its mirror plus
 *     the focus ring in `public/embed.js`. A pasted snippet cannot read our CSS
 *     variables, so it carries the hex. Snippets already on sellers' sites keep
 *     the old colour inline, but the live embed.js overrides it wherever it
 *     loads.
 *  `defaultSettings.accentColor` below uses this constant, so it is not a copy.
 *  src/lib/theme-preference.test.ts pins every copy above except the comment.
 *
 *  NOT a copy, deliberately: slot 1 of the staff analytics palette stays
 *  '#3952d0'. It is a validated data-visualisation palette of its own, not
 *  the site accent.
 *
 *  Drift is silent: the CSS value is what paints for the split second before
 *  the script runs, so a mismatch shows up as a colour flicker on every cold
 *  load and nowhere else. */
export const DEFAULT_ACCENT_COLOR = '#625ff7';

/** The default accent in the DARK theme, hsl(241 100% 75%): the light theme's
 *  hue, lighter, so it shows on the near-black page (6.29:1) and reads as text
 *  there unchanged. Copied into the `.dark` block of `src/app/globals.css`;
 *  src/lib/theme-preference.test.ts pins the two together. */
export const DEFAULT_ACCENT_COLOR_DARK = '#8481ff';

interface ThemeContextType {
  /** Stored preference ('system' | 'light' | 'dark'). Use `resolvedTheme` for what's actually applied. */
  theme: Theme;
  setTheme: (theme: Theme) => void;
  /** Currently applied theme. Always concrete ('dark' or 'light') — 'system' is resolved for you. */
  resolvedTheme: ResolvedTheme;
  /**
   * The viewer's own background toggles, defaulted (src/lib/background-prefs.ts).
   * `showStarfield` is dark mode's background and `showDotGrid` light mode's,
   * both on by default; `showPlanets` and `showPlanetsInLight` are the planets
   * in each mode (on by default in dark mode, off in light) and the gradient
   * is dark-mode only, off by default. What
   * actually renders also depends on the page, the theme, reduced motion and a
   * seller's override: read useCosmicEffects for that, never these, in
   * anything that draws.
   */
  showStarfield: boolean;
  setShowStarfield: (show: boolean) => void;
  showGradient: boolean;
  setShowGradient: (show: boolean) => void;
  showPlanets: boolean;
  setShowPlanets: (show: boolean) => void;
  showPlanetsInLight: boolean;
  setShowPlanetsInLight: (show: boolean) => void;
  showDotGrid: boolean;
  setShowDotGrid: (show: boolean) => void;
  /**
   * False until the viewer's real settings are in: a signed-in user's
   * document has loaded, or auth has definitively resolved to a guest. Until
   * then the toggles above hold the defaults, which may not be the viewer's,
   * so an effect that rendered from them would flash for a viewer who
   * switched it off.
   */
  backgroundPrefsReady: boolean;
  /** The viewer's EXPLICIT gradient direction, or null for automatic. For the preferences control. */
  gradientDirectionPref: GradientDirection | null;
  /** Store an explicit direction, or null for automatic. Call only from a user action. */
  setGradientDirection: (direction: GradientDirection | null) => void;
  /** The viewer's EXPLICIT gradient colour ('#rrggbb'), or null for automatic. For the preferences control. */
  gradientColorPref: string | null;
  /**
   * Store an explicit colour, or null for automatic. Call only from a user
   * action: storing the automatic value would freeze it, and the gradient
   * would stop following the accent and the theme. An invalid hex is ignored.
   */
  setGradientColor: (hex: string | null) => void;
  /**
   * The gradient colour PAINTED on this page: a seller's pick on their own
   * page, then the viewer's pick, then the painted accent. Also set as the
   * --gradient CSS variable, which is what the (dark-mode only) layer uses.
   */
  gradientColor: string;
  /** The gradient direction painted on this page, resolved the same way. */
  gradientDirection: GradientDirection;
  /**
   * Every background switch back to its default (dark mode on, light mode
   * off) and both gradient keys back to automatic, in one write. Deliberately
   * not resetToDefaults, which replaces the whole settings map and would wipe
   * the storefront keys.
   */
  resetBackground: () => void;
  dateFormat: DateFormat;
  setDateFormat: (format: DateFormat) => void;
  /** Format a Date / Timestamp-ish value in the user's chosen short-date
   *  preference. Pass `withTime: true` to append HH:mm in the same style.
   *  Returns an empty string for null / invalid inputs so call sites don't
   *  need to guard. Use this everywhere short numeric dates are rendered;
   *  long-form / time-only strings (e.g. "January 2024", "3:47pm") have a
   *  different intent and stay hardcoded at their call site. */
  formatUserDate: (date: Date | number | string | null | undefined, opts?: { withTime?: boolean }) => string;
  /**
   * The viewer's CHOICE: the account's saved currency when signed in, otherwise
   * the pick remembered in this browser, otherwise the detected one. Not the
   * currency prices are rendered in (that is `displayCurrency`), and not stable
   * across hydration: the server sees neither this browser's storage nor its
   * location signals, so what it resolves can differ. Render `displayCurrency`,
   * never this, in anything the server also renders.
   */
  currency: string;
  /**
   * Saves to the account when signed in. Signed out, remembers the pick in this
   * browser (localStorage `currency`, see src/lib/guest-currency.ts), so it
   * survives a reload, reaches a new tab, and follows into tabs already open.
   */
  setCurrency: (currency: string) => void;
  conversionRates: { [key: string]: number };
  /**
   * Format an amount held in the BASE currency, converted into the viewer's
   * choice. The rate is ALL-IN: it carries the buyer conversion surcharge, so
   * this is the right formatter for a PRICE, i.e. any figure quoting what
   * something will cost. See the `displayRate` comment for why.
   */
  formatCurrency: (amount: number, options?: Omit<Intl.NumberFormatOptions, 'style' | 'currency'>) => string;
  /**
   * Format a BASE-currency amount that records money which has ALREADY moved:
   * receipts, refund receipts, payout records. Converts at the raw market rate,
   * with no surcharge added.
   *
   * Not interchangeable with `formatCurrency`. A charged total already contains
   * the conversion surcharge (a receipt itemises it on its own line), so running
   * it through the all-in rate applies that surcharge a second time and prints a
   * total ~2% above what the card was actually debited, contradicting the
   * "Charged AUD x" figure beside it.
   */
  formatSettledCurrency: (amount: number, options?: Omit<Intl.NumberFormatOptions, 'style' | 'currency'>) => string;
  /**
   * True once a live rate table is in: the one the server rendered with, or one
   * fetched here. There is no stand-in table. Until then only the base currency
   * has a known rate, so anything converted would be
   * a 1:1 figure wearing a foreign symbol. Rendering still works while false —
   * `formatCurrency` falls back to base-currency output — but a call site that
   * wants to hold a figure back until it can be trusted can gate on this.
   */
  ratesReady: boolean;
  /**
   * True when nothing needs converting, either because the viewer picked the base
   * currency or because no rate is known for their choice yet. The AUD footnote
   * beside a converted figure keys on this: when there is no conversion, the
   * footnote would just repeat the headline.
   */
  isBaseCurrency: boolean;
  /**
   * The symbol `formatCurrency` renders with ("US$", "€"), for prefixing an
   * input someone types an amount into. Derived from the formatter itself rather
   * than a second lookup table, so a field's symbol can never disagree with the
   * figures printed beside it.
   */
  currencySymbol: string;
  /**
   * The ISO code `formatCurrency` actually renders in: the viewer's choice, or
   * the base currency when no rate is known for that choice. Spelled out beside
   * a headline total so a bare "$" cannot be read as the wrong dollar.
   */
  displayCurrency: string;
  /** Base-currency amount -> the viewer's display currency, as a number. */
  toDisplayAmount: (baseAmount: number) => number;
  /**
   * The viewer's display currency -> base, as a number. The inverse of
   * `toDisplayAmount`. Every input where someone types money uses this: what is
   * typed is in the currency they can see, what is stored or submitted is base.
   */
  toBaseAmount: (displayAmount: number) => number;
  /**
   * The same amount stated in the base currency ("A$10.00 AUD"), for the footnote
   * under a converted figure. Money actually moves in the base currency, so any
   * screen where a number is acted on (withdrawn, charged, refunded) states it.
   */
  formatBaseAmount: (baseAmount: number) => string;
  refreshRates: () => Promise<void>;
  showNotificationBubbles: boolean;
  setShowNotificationBubbles: (show: boolean) => void;
  showProfileBanner: boolean;
  setShowProfileBanner: (show: boolean) => void;
  showProfilePicture: boolean;
  setShowProfilePicture: (show: boolean) => void;
  opaqueMessages: boolean;
  setOpaqueMessages: (show: boolean) => void;
  showToast: boolean;
  setShowToast: (show: boolean) => Promise<void>;
  toastDurationMs: number;
  setToastDurationMs: (ms: number) => Promise<void>;
  /** BCP-47 language code; empty string means "follow browser". */
  language: string;
  setLanguage: (code: string) => Promise<void>;
  /** Effective language — the saved pref, or the detected browser language when unset. */
  effectiveLanguage: string;
  /** IANA timezone; empty string means "follow browser". */
  timezone: string;
  setTimezone: (tz: string) => Promise<void>;
  /** Effective timezone — the saved pref, or the detected browser zone when unset. */
  effectiveTimezone: string;
  resetToDefaults: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * The currency every stored amount is denominated in: product prices, the
 * double-entry ledger, payouts, receipts. The display currency is a VIEW over
 * this, applied at render time and never persisted, so anything that crosses
 * back into storage or into a callable has to be converted back with
 * `toBaseAmount` first.
 */
export const BASE_CURRENCY = 'AUD';


const defaultSettings: UserSettings = {
    theme: 'system',
    // Each mode's own background is ON by default, the starfield in dark mode
    // and the dot grid in light mode; dark mode's planets are ON too (owner,
    // 2026-10-26), light mode's planets and the gradient OFF, and the gradient
    // never renders in light mode (owner, 2026-10-23).
    // Everything is always off at checkout, whatever anyone chose; that page
    // takes card details. The checkout rule, the per-theme rules and reduced
    // motion all live in resolveCosmicEffects (src/lib/cosmic-effects.ts),
    // not here.
    //
    // WHO THIS OBJECT GOVERNS. Guests only: with no user doc, it IS their
    // settings. A new account's settings are written by the server
    // (createVerifiedUserProfile in functions), which stamps none of the
    // effects, so each reads as its default. An existing account keeps what
    // it has persisted (older accounts hold a stamped `showStarfield: true`
    // and `showGradient: true`); a key it never had reads as its default
    // through backgroundPrefs below (BACKGROUND_DEFAULTS).
    //
    // A seller's page design is read ahead of the viewer's toggles, so a
    // seller can still switch the starfield or gradient on or off for every
    // visitor to their own page in dark mode (planets excepted, see the gate).
    //
    // gradientDirection and gradientColor are deliberately absent: absent is
    // automatic, and nothing but an explicit pick ever stores them.
    showStarfield: BACKGROUND_DEFAULTS.showStarfield,
    showGradient: BACKGROUND_DEFAULTS.showGradient,
    showPlanets: BACKGROUND_DEFAULTS.showPlanets,
    showPlanetsInLight: BACKGROUND_DEFAULTS.showPlanetsInLight,
    showDotGrid: BACKGROUND_DEFAULTS.showDotGrid,
    // YYYY/MM/DD is the product default — date format does NOT follow the
    // browser locale. Empty string is preserved as a legacy "unset" sentinel
    // for older docs, and resolves to the same default at read time below.
    dateFormat: 'YYYY/MM/DD',
    accentColor: DEFAULT_ACCENT_COLOR,
    currency: '',
    showNotificationBubbles: true,
    // On by default since 2026-10-19 (owner), as new accounts are stamped; it
    // only ever shows a banner the account has uploaded.
    showProfileBanner: true,
    showProfilePicture: false,
    opaqueMessages: false,
    showToast: true,
    toastDurationMs: 5000,
    language: '',
    timezone: '',
};

export function ThemeProvider({
  children,
  initialRates = null,
}: {
  children: ReactNode;
  /**
   * Display rates the server read for this page (src/lib/display-rates-server.ts),
   * or null when it had none in time. With them, prices render in the visitor's
   * currency on the server and in the browser's first render alike.
   */
  initialRates?: Record<string, number> | null;
}) {
  const { user, isUserLoading, updateUser } = useAuth();
  // The visitor's IP country, resolved on the server for this request. Its
  // currency is the one the server can render prices in before anything in the
  // browser has run; see serverRenderCurrency below.
  const geoCurrency = currencyForCountry(useGeoCountry());
  // The product page currently open, if it overrides the viewer's appearance.
  // The colours are consumed here (the accent, the forced theme, and the
  // gradient's colour and direction); whether each background effect is on is
  // decided by useCosmicEffects, which reads the override itself.
  // ProductDesignProvider is mounted above this one for exactly this read; see
  // the note in src/app/providers.tsx.
  const { override: designOverride } = useProductDesignOverride();

  // Seeded from the rates the server rendered with, when it had them, so the
  // browser's first render converts exactly as the server did. Without them it
  // holds the base currency only, and every other currency has no rate until
  // the fetch below succeeds.
  const [conversionRates, setConversionRates] = useState<{ [key: string]: number }>(
    () => initialRates ?? { [BASE_CURRENCY]: 1 },
  );
  // False only while the table holds nothing but the base currency. Without
  // this, a first paint for a EUR viewer printed the raw AUD number with a euro
  // symbol on it.
  const [ratesReady, setRatesReady] = useState(() => initialRates !== null);
  // The currency for anyone without a saved account preference: the pick this
  // browser remembered from a signed-out visit (src/lib/guest-currency.ts),
  // else detection (the `gc` geo cookie, then the timezone guess). Only an
  // explicit pick is ever stored, so a visitor who never chose keeps being
  // re-detected on every load.
  //
  // Read in the lazy initialiser, not a mount effect, and that is safe for
  // hydration for the reason detection already was: the server has neither
  // this browser's storage nor its location signals, so `currency` already
  // differed between the server render and the first client render for most
  // visitors. Nothing shows `currency` itself before hydration completes. Every price, symbol and code goes
  // through `displayRate`, which is null (1 for AUD) until the rate table loads
  // in an effect, so the first client render formats in AUD exactly as the
  // server did. The Selects bound to `value={currency}` reach server markup only
  // as Radix's hidden native select, whose value React sets as a property, not
  // an attribute (the storefront footer), or sit in popovers that are closed on
  // the server (marketplace, search). The rest show it only once client-side
  // data has arrived (dashboard pages behind the layout's user gate, receipts
  // after the order loads, dialogs that need a user), and checkout only sends
  // it, as the currency to charge in, without rendering it.
  //
  // An effect would be worse: the currency would change one render after
  // mount, and every consumer keyed on it would take that for a change the
  // viewer made (the checkout discards its quote on exactly that signal).
  //
  // Detection asks the server's IP answer first (preferGeoCurrency), before the
  // browser's own guesses: it is what the server rendered prices in, and a
  // first visit has no `gc` cookie yet, so a timezone that disagrees with the
  // IP would otherwise switch every price to another currency after hydration.
  const detectCurrency = useCallback(
    () => preferGeoCurrency(geoCurrency, detectLocaleCurrency),
    [geoCurrency],
  );
  const [guestCurrency, setGuestCurrency] = useState(
    () => initialGuestCurrency(browserGuestCurrencyStorage(), detectCurrency),
  );
  const [detectedLanguage] = useState(() =>
    typeof navigator !== 'undefined' ? (navigator.language?.split('-')[0] || 'en') : 'en',
  );
  const [detectedTimezone] = useState(() => detectTimezone());

  const settings = user?.settings ?? defaultSettings;

  // Normalised, not read raw. A signed-in user ALWAYS has a `settings` object
  // here (the auth context builds one around isMfaEnabled), so the fallback
  // above never covers a profile whose settings map simply lacks `theme`. Read
  // raw, that `undefined` became the <html> class, was saved to localStorage
  // as "undefined", and crashed checkout in stripeAppearance. See
  // src/lib/theme-preference.ts.
  const theme: Theme = normaliseThemePreference(settings.theme);
  // Defaulted, not read raw, for the same reason: a document missing a key
  // (older accounts for some, every account for the dot grid) would read
  // `undefined`, which is off, where the default is on.
  const { showStarfield, showGradient, showPlanets, showPlanetsInLight, showDotGrid } =
    backgroundPrefs(settings as Record<string, unknown>);
  // The viewer's EXPLICIT gradient picks, or null for automatic. The painted
  // values (which also weigh a seller's page and the theme) are resolved
  // below, once resolvedTheme and the active accent are known.
  const gradientDirectionPref = normaliseGradientDirection(settings.gradientDirection);
  const gradientColorPref = normaliseGradientColor(settings.gradientColor);
  // Until this is true `settings` is defaultSettings standing in for a
  // document that has not arrived, not the viewer's answer. `user` is
  // undefined until auth resolves, then null for a guest or the profile once
  // its first snapshot lands, and isUserLoading covers a sign-in or account
  // switch in flight. See backgroundPrefsReady on the context type.
  const backgroundPrefsReady = user !== undefined && !isUserLoading;
  // Saved preference wins; otherwise fall back to the product default. The
  // settings type permits empty string as a legacy "unset" sentinel for
  // pre-migration docs — those collapse to YYYY/MM/DD here.
  const dateFormat: DateFormat = (settings.dateFormat || 'YYYY/MM/DD') as DateFormat;
  // Signed in: the account's saved preference. Guests, and accounts with no
  // saved currency, get `guestCurrency`: this browser's remembered pick, else
  // detection. That is also what signing out falls back to (see setCurrency).
  const currency = user?.settings?.currency || guestCurrency;
  const showNotificationBubbles = settings.showNotificationBubbles;
  // Absent reads as on, as the storefront reads it (profile-client.tsx).
  const showProfileBanner = settings.showProfileBanner ?? true;
  const showProfilePicture = settings.showProfilePicture ?? false;
  const opaqueMessages = settings.opaqueMessages ?? false;
  const showToast = settings.showToast ?? true;
  const toastDurationMs = settings.toastDurationMs ?? 5000;
  const language = settings.language ?? '';
  const timezone = settings.timezone ?? '';

  // The visitor's own Accent Color setting was retired on 2026-10-21 (owner
  // decision; DESIGN.md, The Violet Is Light Rule). Once buttons, selections
  // and links became ink it tinted only the small lights, and per-visitor
  // colours split the brand: supa.place's own pages now paint the brand violet
  // for everyone, and only a seller's page repaints it, in the seller's accent.
  // The old setting's browser copy is removed once so no stale value lingers;
  // settings.accentColor stays on older account documents, unread.
  useEffect(() => {
    try { localStorage.removeItem(ACCENT_COLOR_STORAGE_KEY); } catch {/* best-effort */}
  }, []);

  // A live table that arrived, merged over what the page already holds so that
  // no live rate is dropped for want of another. The same table again (a retry
  // answered from the cache) keeps the same object, so nothing re-renders.
  const applyRates = useCallback((rates: Record<string, number>) => {
    setConversionRates((held) =>
      Object.keys(rates).some((code) => held[code] !== rates[code]) ? { ...held, ...rates } : held);
    setRatesReady(true);
  }, []);

  // One attempt, on demand. A failure leaves the table exactly as it was:
  // getConversionRates answers null, never a stand-in table.
  const refreshRates = useCallback(async () => {
    const rates = await getConversionRates();
    if (rates) applyRates(rates);
  }, [applyRates]);

  // Fetched in the browser only when the table cannot convert the viewer's
  // choice: the page arrived without rates (the server had none in time), or a
  // signed-in viewer's saved currency is not among the offered ones the server
  // sends. A page that arrived with a rate for the choice asks for nothing.
  //
  // Until the rate arrives, displayRate below stays null, so prices render and
  // are typed in AUD rather than at a guessed rate, and the browser keeps
  // asking: after 15 s, 1 min, 5 min, then every 15 min, and straight away when
  // it comes back online or the tab is shown again (watchConversionRates in
  // src/lib/currencies.ts). A form holding an amount typed in AUD restates it
  // when the rate lands (useHeldDenomination, src/hooks/use-held-denomination.ts),
  // or "20" typed as A$20 would be converted as 20 of the new currency.
  const needsRates = !hasUsableRate(conversionRates, currency);
  useEffect(() => {
    if (!needsRates) return;
    return watchConversionRates({
      covers: (rates) => hasUsableRate(rates, currency),
      onRates: applyRates,
    });
  }, [needsRates, currency, applyRates]);

  // Read OS preference. Tracks live changes so that when theme === 'system'
  // and the user flips their OS theme, the app follows without a reload.
  const getSystemResolved = (): ResolvedTheme =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches
      ? 'dark' : 'light';

  const [systemResolved, setSystemResolved] = useState<ResolvedTheme>(() => getSystemResolved());

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemResolved(e.matches ? 'dark' : 'light');
    // `addEventListener` isn't supported on older Safari — fall back to deprecated `addListener`.
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', handler);
    else mql.addListener(handler);
    return () => {
      if (typeof mql.removeEventListener === 'function') mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, []);

  /** The viewer's own theme, with 'system' resolved against the OS setting. */
  const viewerResolvedTheme: ResolvedTheme = theme === 'system' ? systemResolved : theme;

  /**
   * What the page is ACTUALLY in, which is the viewer's theme unless a product
   * page is open whose seller forced one.
   *
   * Folded in here rather than exposed as a separate value on purpose. Six
   * places already read `resolvedTheme` to answer exactly this question — the
   * `<html>` class below, the starfield and gradient gating in ClientLayout,
   * the accent-as-ink calculation further down, and the appearance handed to
   * Stripe Elements in checkout, the wallet buttons and the diamond dialog. A
   * parallel notion of the theme would leave some of them answering for the
   * buyer and some for the seller on the same page.
   */
  const resolvedTheme: ResolvedTheme = designOverride?.theme ?? viewerResolvedTheme;

  /**
   * The background gradient as PAINTED on this page (it only renders in dark
   * mode): a seller's explicit pick while their page is open, then the
   * viewer's explicit pick, then from below in the accent. See
   * src/lib/background-prefs.ts.
   *
   * The default colour is the ACTIVE accent: the seller's when their page sets
   * one, else the brand accent for the theme being painted, which is what the
   * gradient always followed through --primary.
   */
  const activeAccent = designOverride?.accentColor || (resolvedTheme === 'dark' ? DEFAULT_ACCENT_COLOR_DARK : DEFAULT_ACCENT_COLOR);
  const gradientDirection: GradientDirection = resolveGradientDirection(
    designOverride?.gradientDirection ?? null,
    settings.gradientDirection,
  );
  const gradientColor = resolveGradientColor(
    designOverride?.gradientColor ?? null,
    settings.gradientColor,
    activeAccent,
  );

  /**
   * A seller's solid page background, while their page (or an editor's
   * preview of it) is open: the colour and the panels and text drawn to match
   * it (src/lib/page-background.ts). Its light or dark mode is already folded
   * into `resolvedTheme` above, through the override's `theme`.
   *
   * Written inline on <html> like the accent, so it beats both themes'
   * stylesheet values, and removed property by property when the page closes,
   * which hands every token back to globals.css. Nothing else writes these
   * tokens inline, so removing them cannot undo anyone else's value.
   */
  const pageBackground = designOverride?.backgroundColor ?? null;
  const backgroundPalette = useMemo(() => pageBackgroundPalette(pageBackground), [pageBackground]);
  useEffect(() => {
    if (!backgroundPalette) return;
    const style = window.document.documentElement.style;
    const entries = Object.entries(backgroundPalette.vars);
    for (const [name, value] of entries) style.setProperty(name, value);
    return () => {
      for (const [name] of entries) style.removeProperty(name);
    };
  }, [backgroundPalette]);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedTheme);
    // Mirror the *preference* (not the resolved value) so the pre-hydration
    // script in the root layout can re-apply the correct class on next load.
    //
    // `theme`, deliberately, and this is what stops a seller's forced theme
    // from following the buyer around: the override changes what is PAINTED,
    // never what is STORED, so leaving the product page reverts on the next
    // render and a reload starts from the buyer's own preference either way.
    try { localStorage.setItem(THEME_PREF_STORAGE_KEY, theme); } catch {/* best-effort */}
  }, [resolvedTheme, theme]);

  const setTheme = (newTheme: Theme) => {
    updateUser({ settings: { theme: newTheme } });
    // Persist immediately so a reload before the Firestore round-trip still
    // paints with the right class via the pre-hydration script.
    try { localStorage.setItem(THEME_PREF_STORAGE_KEY, newTheme); } catch {/* best-effort */}
  };
  
  const setShowStarfield = useCallback((show: boolean) => {
    updateUser({ settings: { showStarfield: show }});
  }, [updateUser]);

  const setShowGradient = useCallback((show: boolean) => {
    updateUser({ settings: { showGradient: show }});
  }, [updateUser]);

  const setShowPlanets = useCallback((show: boolean) => {
    updateUser({ settings: { showPlanets: show }});
  }, [updateUser]);

  // The light-mode switches, stored under their own keys so each mode keeps
  // its own choice. Written only from the preferences switches, never defaulted.
  const setShowPlanetsInLight = useCallback((show: boolean) => {
    updateUser({ settings: { showPlanetsInLight: show }});
  }, [updateUser]);

  const setShowDotGrid = useCallback((show: boolean) => {
    updateUser({ settings: { showDotGrid: show }});
  }, [updateUser]);

  // The two gradient setters write ONLY when called, and only the preferences
  // controls call them. There is no local copy, no localStorage mirror and no
  // persistence effect, unlike the accent: storing an automatic value would
  // freeze it (a colour saved from the accent would stop following it).
  const setGradientDirection = useCallback((direction: GradientDirection | null) => {
    updateUser({ settings: { gradientDirection: gradientDirectionSetting(direction) }});
  }, [updateUser]);

  const setGradientColor = useCallback((hex: string | null) => {
    const value = gradientColorSetting(hex);
    if (value === undefined) return;
    updateUser({ settings: { gradientColor: value }});
  }, [updateUser]);

  const resetBackground = useCallback(() => {
    updateUser({ settings: { ...BACKGROUND_RESET_PATCH }});
  }, [updateUser]);
  
  const setDateFormat = (format: DateFormat) => {
    updateUser({ settings: { dateFormat: format }});
  };

  // Single source of truth for short-date rendering. Resolves a Firestore
  // Timestamp-ish value, JS Date, ms epoch, or ISO string into the user's
  // chosen format. Anything we can't coerce to a valid Date returns ''.
  const formatUserDate = useCallback((
    date: Date | number | string | null | undefined,
    opts: { withTime?: boolean } = {},
  ): string => {
    if (date == null) return '';
    let d: Date;
    if (date instanceof Date) d = date;
    else if (typeof date === 'number') d = new Date(date);
    else if (typeof date === 'string') d = new Date(date);
    else return '';
    if (isNaN(d.getTime())) return '';
    // Legacy 2-digit-year keys remain in the lookup so users whose Firestore
    // settings still hold the pre-migration value render as 4-digit until
    // they re-save their preference. Preferences page only writes the new keys.
    const dateMap: Record<string, string> = {
      'DD/MM/YYYY': 'dd/MM/yyyy',
      'MM/DD/YYYY': 'MM/dd/yyyy',
      'YYYY/MM/DD': 'yyyy/MM/dd',
      'DD/MM/YY': 'dd/MM/yyyy',
      'MM/DD/YY': 'MM/dd/yyyy',
      'YY/MM/DD': 'yyyy/MM/dd',
    };
    const basePattern = dateMap[dateFormat] ?? dateMap['YYYY/MM/DD'];
    const pattern = opts.withTime ? `${basePattern} HH:mm` : basePattern;
    try {
      return formatDate(d, pattern);
    } catch {
      return '';
    }
  }, [dateFormat]);

  // Signed in, the pick goes to the account and ONLY there. It is deliberately
  // not mirrored into localStorage (unlike the accent, which is mirrored so the
  // pre-hydration script can paint it): the stored value is what this browser
  // shows once nobody is signed in, and on a shared computer the next visitor
  // should not inherit the last account's currency. So signing out falls back
  // to this browser's own signed-out pick, or detection if there never was
  // one, and signing in hands the display back to the account.
  //
  // Signed out, the pick is remembered. This is the ONLY writer of the guest
  // key, which is what keeps "only an explicit choice is stored" true: every
  // caller is a currency picker the viewer just used. "Signed out" means
  // `!user`, so it also covers the moment before auth resolves and an account
  // still awaiting email verification (no profile yet); a pick made then is
  // remembered like any other guest pick. PreferencesConfirmDialog offers the
  // stored pick to a brand-new account, since the server's IP-first detection
  // would otherwise replace it. A code the pickers do not offer is ignored, so
  // memory and storage cannot disagree.
  const setCurrency = useCallback((currencyCode: string) => {
    if (user) {
      updateUser({ settings: { currency: currencyCode }});
      return;
    }
    if (!isOfferedCurrency(currencyCode)) return;
    setGuestCurrency(currencyCode);
    writeGuestCurrency(browserGuestCurrencyStorage(), currencyCode);
  }, [user, updateUser]);

  // Follow a guest pick made in another tab. The browser fires `storage` only
  // in the tabs that did NOT write, so without this an open storefront tab
  // would keep its old currency until reloaded. The new value is validated like
  // the initial read, and a removed or unusable one falls back to detection, so
  // an open tab ends up where a reload would. Harmless while signed in: the
  // account's currency wins over `guestCurrency` for as long as it is set. An
  // open checkout follows too, and re-quotes, as it does for any currency
  // change (see lastQuotedCurrencyRef in src/app/checkout/checkout-client.tsx).
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      const next = guestCurrencyAfterStorageEvent(event, detectCurrency);
      if (next !== undefined) setGuestCurrency(next);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [detectCurrency]);
  
  const setShowNotificationBubbles = (show: boolean) => {
    updateUser({ settings: { showNotificationBubbles: show }});
  };

  const setShowProfileBanner = (show: boolean) => {
    updateUser({ settings: { showProfileBanner: show } });
  };

  const setShowProfilePicture = (show: boolean) => {
    updateUser({ settings: { showProfilePicture: show } });
  };

  const setOpaqueMessages = (show: boolean) => {
    updateUser({ settings: { opaqueMessages: show }});
  }

  const setShowToast = async (show: boolean) => {
    await updateUser({ settings: { showToast: show } });
  };

  const setToastDurationMsPref = async (ms: number) => {
    const clamped = Math.max(1000, Math.min(10000, Math.round(ms)));
    await updateUser({ settings: { toastDurationMs: clamped } });
  };

  const setLanguage = async (code: string) => {
    await updateUser({ settings: { language: code } });
  };

  const setTimezone = async (tz: string) => {
    await updateUser({ settings: { timezone: tz } });
  };

  const effectiveLanguage = language || detectedLanguage;
  const effectiveTimezone = timezone || detectedTimezone;

  const resetToDefaults = () => {
    updateUser({ settings: defaultSettings }, { replaceSettings: true });
  };

  // The rate for the viewer's choice, or null when there isn't one yet (rates
  // still loading, or a currency the provider did not return). Null is the
  // signal to render in the base currency instead of inventing a 1:1 conversion:
  // printing the unconverted number under a foreign symbol is worse than
  // printing an honest AUD figure, because it is wrong without looking wrong.
  //
  // ALL-IN BY DESIGN. Prices shown in a currency supa.place does not hold carry
  // the buyer currency-conversion surcharge, because that surcharge is charged
  // at checkout and a figure here that omitted it would rise between the product
  // page and the payment step. That is the single most reliable way to lose a
  // sale, so the display rate carries it and the checkout then breaks the same
  // total out into its own line. The server is still authoritative: once the
  // quote lands, checkout renders the server figures verbatim.
  //
  // WHICH CURRENCY PRICES RENDER IN: the viewer's choice, except before
  // hydration. The server cannot see this browser's remembered pick or a
  // signed-in viewer's saved currency, so it renders in serverRenderCurrency:
  // the IP country's currency when it had that rate, else AUD. The browser's
  // first render must print exactly what the server printed, or React redraws
  // the whole page, so it renders in that currency too, and the viewer's own
  // choice takes over straight after. For nearly everyone the two are the same
  // (detection is IP-first as well), so nothing moves. A guest whose remembered
  // pick differs from their IP country's currency, or an account whose saved
  // currency does, sees prices switch once as the page becomes interactive;
  // before, every visitor outside Australia saw them switch from AUD.
  const hydrated = useHydrated();
  const serverRenderCurrency = serverDisplayCurrency(initialRates, geoCurrency);
  const renderCurrency = hydrated ? currency : serverRenderCurrency;
  const displayRate: number | null = (() => {
    if (renderCurrency === BASE_CURRENCY) return 1;
    const rate = conversionRates[renderCurrency];
    if (!(typeof rate === 'number' && Number.isFinite(rate) && rate > 0)) return null;
    return isHeldCurrency(renderCurrency) ? rate : rate * (1 + BUYER_FX_SURCHARGE_RATE);
  })();
  // The same rate WITHOUT the buyer surcharge, for figures that record money
  // which has already moved rather than quoting a price. A receipt's AUD total
  // already contains the surcharge (and itemises it on its own line), so it must
  // be converted at the raw rate or the 2% lands on screen twice. Null under
  // exactly the same conditions as `displayRate`, so `effectiveCurrency` and
  // `isBaseCurrency` stay correct for both.
  const settledRate: number | null = (() => {
    if (renderCurrency === BASE_CURRENCY) return 1;
    const rate = conversionRates[renderCurrency];
    if (!(typeof rate === 'number' && Number.isFinite(rate) && rate > 0)) return null;
    return rate;
  })();
  const isBaseCurrency = displayRate === null || renderCurrency === BASE_CURRENCY;
  const effectiveCurrency = isBaseCurrency ? BASE_CURRENCY : renderCurrency;

  const toDisplayAmount = useCallback((baseAmount: number): number => {
    const n = Number(baseAmount);
    if (!Number.isFinite(n)) return 0;
    return displayRate === null ? n : n * displayRate;
  }, [displayRate]);

  const toBaseAmount = useCallback((displayAmount: number): number => {
    const n = Number(displayAmount);
    if (!Number.isFinite(n)) return 0;
    return displayRate === null ? n : n / displayRate;
  }, [displayRate]);

  // One formatter, two rates. Which rate a figure gets is a real distinction and
  // not a preference: prices convert all-in, money that has already moved
  // converts raw. Keeping the formatting itself in one place means the two can
  // never drift apart on locale, symbol or fallback behaviour.
  const formatAtRate = useCallback((
    rate: number | null,
    amount: number,
    options?: Omit<Intl.NumberFormatOptions, 'style' | 'currency'>,
  ) => {
    const convertedAmount = rate === null ? amount : amount * rate;
    // The fixed pattern table (src/lib/display-currency-format.ts), so the
    // server and every browser print the same characters and a converted price
    // the server rendered hydrates. Every caller formats with the defaults; a
    // caller passing options gets Intl, which is only safe where the server
    // never renders it.
    if (!options) {
      const fixed = formatDisplayCurrency(convertedAmount, effectiveCurrency);
      if (fixed !== null) return fixed;
    }
    const locale = CURRENCY_LOCALE[effectiveCurrency] || 'en-US';
    const formatOptions: Intl.NumberFormatOptions = {
      style: 'currency',
      currency: effectiveCurrency,
      currencyDisplay: 'symbol',
      ...options,
    };
    try {
      return new Intl.NumberFormat(locale, formatOptions).format(convertedAmount);
    } catch {
      const currencyInfo = currencies.find(c => c.code === effectiveCurrency);
      return `${currencyInfo?.symbol || effectiveCurrency}${convertedAmount.toFixed(2)}`;
    }
  }, [effectiveCurrency]);

  const formatCurrency = useCallback((
    amount: number,
    options?: Omit<Intl.NumberFormatOptions, 'style' | 'currency'>,
  ) => formatAtRate(displayRate, amount, options), [formatAtRate, displayRate]);

  const formatSettledCurrency = useCallback((
    amount: number,
    options?: Omit<Intl.NumberFormatOptions, 'style' | 'currency'>,
  ) => formatAtRate(settledRate, amount, options), [formatAtRate, settledRate]);

  // Strips the digits and the grouping/decimal separators out of a formatted
  // zero, leaving the symbol. The character class also covers the non-breaking
  // and narrow-no-break spaces Intl puts between amount and symbol in several
  // locales, so a stray one cannot end up floating inside an input's prefix.
  const currencySymbol = useMemo(
    () => formatCurrency(0).replace(/[\d.,\s  ]/g, ''),
    [formatCurrency],
  );

  // "A$10.00 AUD". The code is spelled out alongside the symbol on purpose: this
  // string only ever appears next to a converted headline, where its whole job is
  // to name the currency the money actually moves in.
  const formatBaseAmount = useCallback((baseAmount: number): string => {
    const n = Number(baseAmount);
    if (!Number.isFinite(n)) return '—';
    try {
      return `${formatMoney(fromDecimal(n, BASE_CURRENCY))} ${BASE_CURRENCY}`;
    } catch {
      return `A$${n.toFixed(2)} ${BASE_CURRENCY}`;
    }
  }, []);

  /**
   * The ONE writer of the accent custom properties.
   *
   * supa.place's own pages paint the brand accent for everyone,
   * DEFAULT_ACCENT_COLOR in light and DEFAULT_ACCENT_COLOR_DARK in dark (the
   * visitor's Accent Color setting was retired on 2026-10-21).
   * A seller's page that sets an accent publishes it through
   * ProductDesignProvider, and it wins for exactly as long as that page is
   * mounted. The page clears the override on unmount, which re-runs this
   * effect and restores the brand violet, so there is no separate restore path
   * that could be missed on a navigation, a back button, or an unmount during
   * a route transition.
   *
   * The override arrives pre-clamped so its label reads (see
   * resolveDesignOverride), and is lifted here if it would vanish into the
   * page (see visibleAccentHex below).
   *
   * globals.css paints the brand violet before React mounts, so a seller's
   * page shows violet for its first frame and the seller's accent from here
   * on (the same behaviour the starfield override has).
   *
   * `--gradient` is written here too, for the same single-writer reason. Its
   * CSS default in globals.css is the default accent; this effect depends on
   * the resolved gradient colour, which moves with the accent, the viewer's
   * sky settings and a seller's override. (The layer only renders in dark mode.)
   */
  useEffect(() => {
    // Lifted, only if it must be, until a button filled with it shows against
    // the page it sits on (the seller's background colour, else the theme's
    // page); a near-black seller accent otherwise drew a buy button that
    // vanished into a dark page. The brand violet passes unchanged.
    const pageHex = pageBackground ?? PAGE_HEX[resolvedTheme === 'dark' ? 'dark' : 'light'];
    const active = visibleAccentHex(activeAccent, pageHex) || activeAccent;
    const hslColor = hexToHsl(active);
    document.documentElement.style.setProperty('--primary', hslColor);
    document.documentElement.style.setProperty('--chart-1', hslColor);
    // The label set on it (the buy button, a count badge), picked by contrast:
    // a seller's clamped accent keeps its near-white label, the brand's light
    // accent takes pure white and its dark one the dark label.
    document.documentElement.style.setProperty('--primary-foreground', hexToHsl(accentLabelHex(active)));

    /**
     * The same accent, made legible as INK rather than as a surface.
     *
     * `--primary` is correct where the accent is a background with
     * `--primary-foreground` on top, which is what clampAccentHex guarantees.
     * It is not correct as a text colour: measured, every clamped accent
     * landed under AA against the dark surfaces the product page draws its tag
     * chips and links on, and darkening a bright pick for button legibility
     * made that strictly worse. See readableAccentHex.
     *
     * Depends on `resolvedTheme` because the answer genuinely differs by
     * theme — the ink moves lighter on dark and darker on light — so this
     * effect re-runs when the viewer switches themes.
     *
     * `--ring` follows the readable value rather than `--primary`: a focus
     * ring is a thin mark read against the page, so it has the legibility
     * problem of ink and none of the guarantees of a surface.
     *
     * On a seller's background colour the surfaces are that colour's own
     * shades rather than the theme's, so the ink is measured against those.
     */
    const readable = readableAccentHex(active, resolvedTheme === 'dark', backgroundPalette?.textSurfaces) || active;
    const readableHsl = hexToHsl(readable);
    document.documentElement.style.setProperty('--primary-readable', readableHsl);
    document.documentElement.style.setProperty('--ring', readableHsl);

    // An HSL triplet like --primary, so Tailwind's `gradient` colour takes an
    // alpha modifier (`to-gradient/20`) the same way `primary` does.
    document.documentElement.style.setProperty('--gradient', hexToHsl(gradientColor));
  }, [activeAccent, gradientColor, resolvedTheme, backgroundPalette, pageBackground]);

  /**
   * A seller's page hands buttons, selections, controls and links to the
   * seller's accent through `[data-seller-accent]` (src/app/globals.css), but
   * only when the seller picked one: a seller with no accent gets the site's
   * ink, never the visitor's own accent painted onto someone else's page.
   *
   * On <body>, not on a page wrapper, because dialogs, popovers and toasts
   * portal to <body> and are the seller's page too (a buyer's Download dialog,
   * the review dialog), and so is the storefront nav beside its <main>. Set in
   * the same commit as the effect above writes the accent into `--primary`,
   * so the two never disagree. Every caller of setDesignOverride is a seller's
   * page or an editor's preview of one (product-designability.test.ts checks).
   */
  useEffect(() => {
    const body = document.body;
    body.toggleAttribute('data-seller-accent', !!designOverride?.accentColor);
    return () => body.removeAttribute('data-seller-accent');
  }, [designOverride]);

  useEffect(() => {
    setToastDurationMs(toastDurationMs);
  }, [toastDurationMs]);

  const value = {
    theme, setTheme, resolvedTheme,
    showStarfield, setShowStarfield,
    showGradient, setShowGradient,
    showPlanets, setShowPlanets,
    showPlanetsInLight, setShowPlanetsInLight,
    showDotGrid, setShowDotGrid,
    backgroundPrefsReady,
    gradientDirectionPref, setGradientDirection,
    gradientColorPref, setGradientColor,
    gradientColor, gradientDirection,
    resetBackground,
    dateFormat, setDateFormat, formatUserDate,
    currency, setCurrency,
    conversionRates,
    formatCurrency, formatSettledCurrency,
    ratesReady, isBaseCurrency, currencySymbol,
    displayCurrency: effectiveCurrency,
    toDisplayAmount, toBaseAmount, formatBaseAmount,
    refreshRates,
    showNotificationBubbles, setShowNotificationBubbles,
    showProfileBanner, setShowProfileBanner,
    showProfilePicture, setShowProfilePicture,
    opaqueMessages, setOpaqueMessages,
    showToast, setShowToast,
    toastDurationMs, setToastDurationMs: setToastDurationMsPref,
    language, setLanguage, effectiveLanguage,
    timezone, setTimezone, effectiveTimezone,
    resetToDefaults,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

    